import { AppointmentResponse } from "@9amhealth/openapi";
import { Button, isDeprecatedAppointmentType } from "@9amhealth/shared";
import { useBloc } from "@blac/react";
import styled from "@emotion/styled";
import { getLocalTimeZone } from "@internationalized/date";
import { IonButton, IonModal } from "@ionic/react";
import { Blac } from "blac-next";
import Scheduler from "molecule/scheduler/Scheduler";
import {
  ScheduleAppointmentTypes,
  SchedulerBloc,
  SchedulerReconnectStatus
} from "molecule/scheduler/SchedulerBloc";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { IconCloseCircle } from "src/constants/icons";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import AppointmentsBloc from "src/state/AppointmentsBloc/AppointmentsBloc";
import { tracker } from "src/state/state";
import TrackCubit from "src/state/Track/TrackCubit";
import { TranslationKey } from "src/types/translationKey";
import AppointmentIcon from "../AppointmentIcon/AppointmentIcon";
import { AppQueryPopupsController } from "../AppQueryPopups/AppQueryPopupsBloc";
import Avatar from "../Avatar/Avatar";
import BlockingLoadingOverlayController from "../BlockingLoadingOverlay/BlockingLoadingOverlayController";
import Loader from "../Loader/Loader";
import ProviderReconnectModal from "../ProviderReconnectModal/ProviderReconnectModal";
import Translate from "../Translate/Translate";
import { extractErrorCode } from "src/lib/errors";
import UserPreferencesCubit from "src/state/UserPreferencesCubit/UserPreferencesCubit";

const Wrap = styled.div`
  padding: 0 1.6rem;
  margin: 0 auto 5rem;
  width: min(100%, 520px);
`;

const ErrorInfo = styled.div`
  margin: 0 auto;
  width: min(100%, 520px);
  padding: 1rem;
  border-radius: 0.5rem;
  text-align: center;
  display: flex;
  gap: 1rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;

  h4 {
    text-wrap: balance;
  }
`;

const Heading = styled.div`
  text-align: center;
  margin: 2rem 0 3rem;
  scroll-margin-top: 100vh;

  .appointment-icon svg {
    width: 4rem;
    height: 4rem;
    margin-bottom: 1rem;
    display: block;
    margin: 0 auto;
  }

  h2 {
    font-size: 2.25rem;
    margin-top: 2rem;
    text-wrap: pretty;
  }

  p {
    font-size: 1rem;
    margin-top: 0.8rem;
    padding: 0 1rem;
  }

  .slot-details {
    margin: 2rem 0;
    align-items: center;
  }
`;

const AvailabilitySection = styled.div`
  scroll-margin-top: 2rem;
  margin: 5rem 0;

  h3 {
    font-size: 1.2rem;
    margin-bottom: 1rem;
    font-weight: 500;
    font-family: var(--font-family);
  }
`;

const StyledSchedulerConfirmSlot = styled.div`
  min-height: 100%;
`;

const AppointmentScheduler: FC<{
  type?: ScheduleAppointmentTypes;
  rescheduleId?: string;
  reschedulingRequired?: boolean;
  onScheduled?: () => void;
}> = ({ type, onScheduled, rescheduleId, reschedulingRequired }) => {
  const [
    {
      loading,
      selectedDate,
      availableSlotGroups,
      selectedSlot,
      selectedSlotGroup,
      previousProvider,
      reconnectStatus
    },
    {
      setSelectedSlot,
      bookSelectedSlot,
      fetchPreviousProvider,
      handlePreferenceChangeReconnectProvider,
      showSelectProvider,
      getActiveStep
    }
  ] = useBloc(SchedulerBloc, {
    props: {
      availableSlots: [],
      onSelectDate: (date) =>
        tracker.track("Scheduler Date Selected", {
          data: {
            ...getCommonTrackingProps(),
            selectedDate: TrackCubit.formatDate(date)
          }
        }),
      onAcceptReconnectProvider: (reconnectStatus) =>
        tracker.track("Scheduler Reconnect Provider Accepted", {
          data: {
            ...getCommonTrackingProps(),
            reconnectStatus
          }
        }),
      onRejectReconnectProvider: (reconnectStatus) =>
        tracker.track("Scheduler Reconnect Provider Rejected", {
          data: {
            ...getCommonTrackingProps(),
            reconnectStatus
          }
        }),
      onPreviousProviderDismissed: (reconnectStatus) =>
        tracker.track("Scheduler Previous Provider Dismissed", {
          data: {
            ...getCommonTrackingProps(),
            reconnectStatus
          }
        }),
      onSelectSlotGroup: (slotGroup) =>
        tracker.track("Scheduler Time Selected", {
          data: {
            ...getCommonTrackingProps(),
            slotTime: TrackCubit.formatDateTime(slotGroup.dateTime),
            slotDuration: slotGroup.duration,
            slotProviderIds: slotGroup.slotProviderIds,
            slotProviderNames: slotGroup.slotProviderNames
          }
        }),
      onSelectSlot: (slot) =>
        tracker.track("Scheduler Provider Selected", {
          data: {
            ...getCommonTrackingProps(),
            autoSelected: slot.autoSelected ?? false,
            slotTime: TrackCubit.formatDateTime(slot.dateTime),
            slotDuration: slot.duration,
            slotProviderId: slot.slotProviderId,
            slotProviderName: slot.slotProviderName
          }
        }),
      onDeselectSlot: () =>
        tracker.track("Scheduler Confirmation Closed", {
          data: {
            ...getCommonTrackingProps()
          }
        }),
      onSelectProvider: (provider) =>
        tracker.track("Scheduler Provider Selected", {
          data: {
            ...getCommonTrackingProps(),
            providerId: provider.id,
            providerName: provider.name
          }
        }),
      onAppointmentBooked: (appointment) =>
        tracker.track("Scheduler Appointment Booked", {
          data: {
            ...getCommonTrackingProps(),
            appointmentId: appointment.id,
            appointmentDateTime: TrackCubit.formatDateTime(
              appointment.dateTime
            ),
            appointmentDuration: appointment.duration
          }
        }),
      onAppointmentRescheduled: (oldAppointment, newAppointment) =>
        tracker.track("Scheduler Appointment Rescheduled", {
          data: {
            ...getCommonTrackingProps(),
            appointmentId: newAppointment.id,
            oldAppointmentDateTime: TrackCubit.formatDateTime(
              oldAppointment.dateTime
            ),
            oldAppointmentDuration: oldAppointment.duration,
            newAppointmentDateTime: TrackCubit.formatDateTime(
              newAppointment.dateTime
            ),
            newAppointmentDuration: newAppointment.duration
          }
        })
    }
  });

  const [appointmentType, setAppointmentType] = useState<
    ScheduleAppointmentTypes | undefined
  >(type);
  const [rescheduleAppointment, setRescheduleAppointment] = useState<
    AppointmentResponse | undefined
  >();
  const [error, setError] = useState<TranslationKey | undefined>(
    !rescheduleId && (!type || isDeprecatedAppointmentType(type))
      ? "error_appointment_details_load_failed"
      : undefined
  );
  const scheduleSuccess = useRef(false);
  const isMounted = useRef(true);
  const trackedOpen = useRef(false);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    Blac.getBloc(SchedulerBloc).reschedulingRequired = reschedulingRequired;
  }, [reschedulingRequired]);

  useEffect(() => {
    if (appointmentType && typeof previousProvider !== "undefined") {
      if (!trackedOpen.current) {
        tracker.track("Scheduler Opened", {
          data: {
            ...getCommonTrackingProps(),
            appointmentId: rescheduleId
          }
        });
        trackedOpen.current = true;
      }
      return () => {
        const useSelectedDate = selectedDate;
        const useSelectedSlotTime = selectedSlot?.from;
        setTimeout(() => {
          if (!scheduleSuccess.current && !isMounted.current) {
            tracker.track("Scheduler Abandoned", {
              data: {
                ...getCommonTrackingProps(),
                selectedDate: TrackCubit.formatDate(useSelectedDate),
                selectedSlotTime:
                  TrackCubit.formatDateTime(useSelectedSlotTime),
                activeStep: getActiveStep()
              }
            });
          }
        }, 100);
      };
    }
  }, [
    type,
    rescheduleId,
    appointmentType,
    previousProvider,
    selectedDate,
    selectedSlot,
    reconnectStatus
  ]);

  /**
   * requestProviders
   * @description
   * undefined means we haven't asked the question yet
   * false means we don't filter for providers
   * string[] means we filter for these providers
   */
  const [requestProviders, setRequestProviders] = useState<
    string[] | false | undefined
  >();

  const handleSetRequestProviders = (
    providers: string[] | false,
    action: SchedulerReconnectStatus
  ) => {
    setRequestProviders(providers);
    handlePreferenceChangeReconnectProvider(action);
  };

  const availabilitySectionRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const slotProviderSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (appointmentType && typeof previousProvider === "undefined") {
      void fetchPreviousProvider(appointmentType);
    }
  }, [appointmentType, previousProvider]);

  const participantsNames = useMemo(() => {
    if (!rescheduleAppointment) {
      return [];
    }
    return rescheduleAppointment.participants
      .map((p) => p.displayName)
      .filter(Boolean);
  }, [rescheduleAppointment]);

  const titleContext = rescheduleId
    ? participantsNames.length > 0
      ? "rescheduleNamed"
      : "reschedule"
    : "schedule";

  const showAvailability = selectedDate && availableSlotGroups.length > 0;

  // #region Scroll to top
  useEffect(() => {
    setTimeout(() => {
      if (headingRef.current) {
        headingRef.current.scrollIntoView({
          behavior: "smooth"
        });
      }
    }, 60);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      if (availabilitySectionRef.current) {
        availabilitySectionRef.current.scrollIntoView({
          behavior: "smooth"
        });
      }
    }, 60);
  }, [selectedDate]);

  useEffect(() => {
    setTimeout(() => {
      if (slotProviderSectionRef.current) {
        slotProviderSectionRef.current.scrollIntoView({
          behavior: "smooth"
        });
      }
    }, 60);
  }, [selectedSlotGroup]);
  // #endregion

  // #region Handlers
  const handleDismissSlotDetailModal = () => {
    setSelectedSlot(undefined);
  };

  const handleBookAppointment = () => {
    BlockingLoadingOverlayController.startLoading({
      bg: "transparent",
      title: "appointment.loading.booking",
      message: "appointment.loading.booking.description"
    });

    void bookSelectedSlot({
      rescheduleAppointment
    })
      .then((data) => {
        Blac.getBloc(AppointmentsBloc).addAppointment(data);
        scheduleSuccess.current = true;
        if (onScheduled) {
          onScheduled();
        }
        const userPreferences = Blac.getBloc(UserPreferencesCubit);
        BlockingLoadingOverlayController.loadingSuccess({
          title: "appointment.loading.success",
          message: translate("appointment.loading.success.description", {
            name: userPreferences.firstName
          })
        });
      })
      .catch((e: unknown) => {
        reportErrorSentry(e);
        BlockingLoadingOverlayController.loadingError({
          title: translate("error_generic_title"),
          message: translate("appointment.scheduling.error", {
            context: extractErrorCode(e)
          }),
          retry: () => {
            handleBookAppointment();
          }
        });
      });
  };
  // #endregion

  useEffect(() => {
    let mounted = true;
    if (rescheduleId && !appointmentType && !rescheduleAppointment) {
      Blac.getBloc(AppointmentsBloc)
        .getAppointmentById(rescheduleId)
        .then((appointment) => {
          if (!mounted) {
            return;
          }
          if (!appointment) {
            setError("error_appointment_details_load_failed");
            return;
          }
          if (isDeprecatedAppointmentType(appointment.type)) {
            setError("error_appointment_details_load_failed");
            return;
          }
          setAppointmentType(appointment.type);
          setRescheduleAppointment(appointment);
        })
        .catch((e: unknown) => {
          reportErrorSentry(e);
        });
    }

    return () => {
      mounted = false;
    };
  }, [rescheduleId, appointmentType, rescheduleAppointment]);

  function getCommonTrackingProps() {
    return {
      mode: rescheduleId ? "reschedule" : "new",
      appointmentType,
      previousProviderId: previousProvider?.userId,
      previousProviderName: previousProvider?.displayName,
      reconnectStatus
    };
  }

  if (error) {
    return (
      <ErrorInfo>
        <h4>{translate(error)}</h4>
        <Button
          hideArrow
          theme="charcoal"
          onPress={(): void => AppQueryPopupsController.closePopup()}
        >
          <Translate msg="close" />
        </Button>
      </ErrorInfo>
    );
  }

  if (
    !appointmentType ||
    (typeof previousProvider === "undefined" && !rescheduleAppointment)
  ) {
    return (
      <Loader active={true} fullPage bg="transparent" fixed hideProgress />
    );
  }

  if (
    typeof requestProviders === "undefined" &&
    previousProvider &&
    !rescheduleAppointment
  ) {
    return (
      <>
        <ProviderReconnectModal
          providerName={previousProvider.displayName || ""}
          providerId={previousProvider.userId}
          onYes={() =>
            handleSetRequestProviders([previousProvider.userId], "accepted")
          }
          onNo={() => handleSetRequestProviders(false, "rejected")}
        />
      </>
    );
  }

  return (
    <Wrap>
      <Heading ref={headingRef}>
        <div className="appointment-icon">
          <AppointmentIcon type={appointmentType} />
        </div>
        <h2>
          {translate("appointment.title", {
            context: titleContext,
            name: translate("appointment.name", {
              context: appointmentType.toLowerCase().replace("_", "-")
            }),
            participants: participantsNames
          })}
        </h2>
      </Heading>

      <Loader active={loading}>
        {previousProvider && requestProviders && (
          <Scheduler
            view="reuse-provider"
            avatar={(id) => <Avatar id={id} />}
            id={previousProvider.userId}
            name={previousProvider.displayName || ""}
            onClose={() => handleSetRequestProviders(false, "dismissed")}
          />
        )}

        <Scheduler
          view="pick-date"
          type={appointmentType}
          rescheduleAppointment={rescheduleAppointment}
          requestProviders={requestProviders || undefined}
        />

        {showAvailability && (
          <AvailabilitySection ref={availabilitySectionRef}>
            <h3>
              <Translate
                msg="appointment.schedule.available_on"
                variables={{
                  date: selectedDate
                    .toDate(getLocalTimeZone())
                    .toLocaleDateString("en-us", {
                      month: "short",
                      weekday: "short",
                      day: "numeric",
                      year: "numeric"
                    })
                }}
              />
            </h3>
            <Scheduler view="pick-slot" />
          </AvailabilitySection>
        )}

        {showSelectProvider && (
          <AvailabilitySection ref={slotProviderSectionRef}>
            <h3>
              <Translate msg="appointment.schedule.select_provider" />
            </h3>
            <Scheduler
              view="pick-provider"
              avatar={(id) => <Avatar id={id} />}
            />
          </AvailabilitySection>
        )}

        <IonModal
          isOpen={Boolean(selectedSlot)}
          onDidDismiss={handleDismissSlotDetailModal}
          initialBreakpoint={0.7}
          breakpoints={[0, 0.7]}
          className="selected-search-result-info"
        >
          <IonButton
            aria-label="Close"
            className="close-modal-button"
            onClick={handleDismissSlotDetailModal}
          >
            <IconCloseCircle />
          </IonButton>

          <StyledSchedulerConfirmSlot>
            <Scheduler
              view="confirm-slot"
              avatar={(id) => <Avatar id={id} />}
              handleBookAppointment={handleBookAppointment}
              rescheduleAppointment={rescheduleAppointment}
              bookAppointmentText={
                rescheduleAppointment ? (
                  <Translate msg="action.rescheduleAppointment" />
                ) : (
                  <Translate msg="action.bookAppointment" />
                )
              }
              scheduleInfo={
                <p>
                  <Translate msg="appointment.info.cancellation.policy" />
                </p>
              }
            />
          </StyledSchedulerConfirmSlot>
        </IonModal>
      </Loader>
    </Wrap>
  );
};

export default AppointmentScheduler;
